<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { AiChatSessionsService } from '@/types/generated'
  import { FILE_INPUT_ACCEPT } from '@/constants/documentFileTypes'
  import {
    ArrowUp,
    FolderInput,
    Info,
    ListRestart,
    Paperclip,
    Plus,
    Section
  } from 'lucide-vue-next'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { Switch } from '@/components/ui/switch'
  import type { AvailableModel } from '@/types/generated'
  import ModelSelector from '@/components/app/chat/components/ModelSelector.vue'
  import SparkleIcon from './SparkleIcon.vue'
  import InputBadge from './InputBadge.vue'
  import CaseSearchPanel from './CaseSearchPanel.vue'
  import CasePreviewDialog from './CasePreviewDialog.vue'

  export interface ChatInputBadge {
    id: string
    label: string
    variant: 'case' | 'pdf' | 'word' | 'other' | 'court-db'
    removable?: boolean
    loading?: boolean
  }

  /** Maximum number of document badges shown before collapsing with "+N". */
  const MAX_VISIBLE_DOCS = 2

  const props = defineProps<{
    /** Disables input while AI is generating a response. */
    disabled?: boolean
    /** Whether the court database feature flag is enabled. */
    courtDatabaseFlagEnabled?: boolean
    /** Whether the court database toggle is currently on. */
    courtDbEnabled?: boolean
    /** Badges displayed above the textarea (e.g. attached cases or documents). */
    badges?: ChatInputBadge[]
    /** The manually selected case context (null = none). */
    selectedCase?: { uuid: string | null; label: string } | null
    /** Whether the case is locked (messages already sent with this case). */
    caseLocked?: boolean
    /** Whether document uploads are currently in progress. */
    uploadsInProgress?: boolean
    /** Currently selected model ID (null = Auto). */
    modelId?: string | null
    /** Available models for the dropdown. */
    availableModels?: AvailableModel[]
    /**
     * Analytics chat context for the ModelSelector.
     * Derived in the parent from unifiedChat.hasContextCase so that
     * model selections inside the side panel on /app/cases/:uuid fire
     * as 'case' rather than being undercounted as 'general'.
     */
    chatContext: 'case' | 'general'
  }>()

  const toggleCourtDb = () => {
    emit('toggleCourtDb')
  }

  const plusMenuOpen = ref(false)
  const caseSearchOpen = ref(false)
  const caseSearchQuery = ref('')
  const isEnhancing = ref(false)

  // Case preview dialog state
  const previewOpen = ref(false)
  const previewCaseUuid = ref<string | null>(null)
  const previewCaseLabel = ref('')
  const shouldReopenCaseSearchAfterPreview = ref(false)

  function onCasePreview(uuid: string, label: string) {
    shouldReopenCaseSearchAfterPreview.value = true
    plusMenuOpen.value = false
    previewCaseUuid.value = uuid
    previewCaseLabel.value = label
    previewOpen.value = true
  }

  function onPreviewAddToChat(uuid: string, label: string) {
    shouldReopenCaseSearchAfterPreview.value = false
    previewOpen.value = false
    plusMenuOpen.value = false
    emit('selectCase', uuid, label)
  }

  // Reset case search view when popover closes
  watch(plusMenuOpen, (open) => {
    if (!open) caseSearchOpen.value = false
  })

  watch(previewOpen, (open, wasOpen) => {
    if (open || !wasOpen || !shouldReopenCaseSearchAfterPreview.value) return
    plusMenuOpen.value = true
    caseSearchOpen.value = true
    shouldReopenCaseSearchAfterPreview.value = false
  })

  const canEnhance = computed(
    () => message.value.trim().length > 0 && !props.disabled && !isEnhancing.value
  )

  async function enhancePrompt() {
    if (!canEnhance.value) return

    isEnhancing.value = true
    try {
      const response = await AiChatSessionsService.aiChatSessionsEnhancePromptCreate({
        requestBody: { content: message.value.trim() }
      })
      message.value = response.enhanced_content
      nextTick(resizeTextarea)
    } catch (error) {
      console.error('Failed to enhance prompt:', error)
    } finally {
      isEnhancing.value = false
    }
  }

  const emit = defineEmits<{
    (e: 'send', message: string): void
    (e: 'openPrompts'): void
    (e: 'toggleCourtDb'): void
    (e: 'uploadDocuments', files: File[]): void
    (e: 'removeBadge', id: string): void
    (e: 'selectCase', uuid: string, label: string): void
    (e: 'removeCase'): void
    (e: 'update:modelId', value: string | null): void
  }>()

  const fileInputRef = ref<HTMLInputElement | null>(null)

  function triggerFilePicker() {
    plusMenuOpen.value = false
    fileInputRef.value?.click()
  }

  function onFileInput(event: Event) {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files ?? [])
    if (files.length > 0) {
      emit('uploadDocuments', files)
    }
    // Reset so the same file can be re-selected
    input.value = ''
  }

  const message = ref('')
  const textareaRef = ref<HTMLTextAreaElement | null>(null)
  const isTextareaScrollable = ref(false)
  const canSend = computed(
    () => message.value.trim().length > 0 && !props.disabled && !props.uploadsInProgress
  )

  const TEXTAREA_MAX_HEIGHT = 270

  function resizeTextarea() {
    const el = textareaRef.value
    if (!el) return
    el.style.height = '24px'
    const newHeight = Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT)
    el.style.height = `${newHeight}px`
    isTextareaScrollable.value = el.scrollHeight > TEXTAREA_MAX_HEIGHT
    el.style.overflowY = isTextareaScrollable.value ? 'auto' : 'hidden'
  }

  watch(message, () => nextTick(resizeTextarea))

  function handleSend() {
    if (!canSend.value) return
    emit('send', message.value.trim())
    message.value = ''
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      handleSend()
    }
  }

  function setMessage(text: string) {
    message.value = text.replace(/^\s+/, '')
    nextTick(() => {
      resizeTextarea()
      textareaRef.value?.focus()
    })
  }

  function focusInput() {
    nextTick(() => textareaRef.value?.focus())
  }

  onMounted(() => {
    focusInput()
  })

  /**
   * All non-document badges (case, court-db).
   *
   * Order is intentional and stable: the case context badge is **always pinned to
   * position 1** — case-variant entries from ``props.badges`` are emitted first so the
   * conversation's primary context stays leftmost regardless of whether court-database
   * was toggled on before the case was attached. Court-database trails any case badge,
   * and any other non-doc variants from props (forward-compatible) come last. Keep this
   * contract in sync with :file:`AiChatMessageInputAssistant.vue`.
   */
  const nonDocBadges = computed<ChatInputBadge[]>(() => {
    const result: ChatInputBadge[] = []
    const propsNonDoc =
      props.badges?.filter((b) => !['pdf', 'word', 'other'].includes(b.variant)) ?? []
    const caseBadges = propsNonDoc.filter((b) => b.variant === 'case')
    const otherBadges = propsNonDoc.filter((b) => b.variant !== 'case')
    result.push(...caseBadges)
    if (props.courtDatabaseFlagEnabled && props.courtDbEnabled) {
      result.push({ id: '__court-db__', label: t('courtDatabase'), variant: 'court-db' })
    }
    result.push(...otherBadges)
    return result
  })

  /** All document badges from props. */
  const docBadges = computed<ChatInputBadge[]>(() =>
    (props.badges ?? []).filter((b) => ['pdf', 'word', 'other'].includes(b.variant))
  )

  /** Visible document badges (first N). */
  const visibleDocBadges = computed(() => docBadges.value.slice(0, MAX_VISIBLE_DOCS))

  /** Number of hidden document badges. */
  const hiddenDocCount = computed(() => Math.max(0, docBadges.value.length - MAX_VISIBLE_DOCS))

  const hasBadges = computed(() => nonDocBadges.value.length > 0 || docBadges.value.length > 0)

  function onRemoveBadge(id: string) {
    if (id === '__court-db__') {
      emit('toggleCourtDb')
    } else if (id === '__case__') {
      emit('removeCase')
    } else {
      emit('removeBadge', id)
    }
  }

  function onCaseSelected(uuid: string, label: string) {
    plusMenuOpen.value = false
    emit('selectCase', uuid, label)
  }

  defineExpose({ setMessage, getMessage: () => message.value, focus: focusInput })

  const { t } = useTranslation({
    en: {
      placeholder: 'Start a task or ask a question...',
      send: 'Send',
      prompts: 'Prompts',
      addDocuments: 'Add documents',
      addRequest: 'Add request',
      courtDatabase: 'Court database',
      more: 'more',
      caseAlreadyAdded:
        'This request has already been added. Please start a new chat for additional requests.',
      legal:
        'JUPUS is an AI and can make mistakes. Please verify answers. Conversations are visible to colleagues.'
    },
    de: {
      placeholder: 'Aufgabe starten oder fragen...',
      send: 'Senden',
      prompts: 'Prompts',
      addDocuments: 'Dokumente hinzufügen',
      addRequest: 'Anfrage hinzufügen',
      courtDatabase: 'Urteilsdatenbank',
      more: 'weitere',
      caseAlreadyAdded:
        'Diese Anfrage wurde bereits hinzugefügt. Für weitere Anfragen bitte neuen Chat starten.',
      legal:
        'JUPUS ist eine KI und kann Fehler machen. Bitte überprüfen Sie die Antworten. Konversationen sind für Kollegen sichtbar.'
    },
    fr: {
      placeholder: 'Démarrer une tâche ou poser une question...',
      send: 'Envoyer',
      prompts: 'Modèles',
      addDocuments: 'Ajouter des documents',
      addRequest: 'Ajouter une demande',
      courtDatabase: 'Base de données judiciaire',
      more: 'autres',
      caseAlreadyAdded:
        'Cette demande a déjà été ajoutée. Veuillez démarrer une nouvelle conversation pour des demandes supplémentaires.',
      legal:
        'JUPUS est une IA et peut faire des erreurs. Veuillez vérifier les réponses. Les conversations sont visibles par vos collègues.'
    }
  })
</script>

<template>
  <div class="px-4 pb-4">
    <div
      data-prompt-text="true"
      data-property-1="Default"
      class="flex w-full flex-col items-start justify-start gap-4 rounded-xl bg-white px-3 pb-3 outline outline-1
        -outline-offset-[0.5px] outline-gray-200 focus-within:outline-purple-600"
      :class="isTextareaScrollable ? 'pt-1' : 'pt-3'"
    >
      <!-- Badges (documents on first row, case/court-db on second row) -->
      <div
        v-if="hasBadges"
        class="flex flex-col items-start gap-2 self-stretch"
      >
        <!-- Document badges row with +N overflow -->
        <div
          v-if="docBadges.length"
          class="flex flex-wrap items-center gap-2 self-stretch"
        >
          <InputBadge
            v-for="badge in visibleDocBadges"
            :key="badge.id"
            :label="badge.label"
            :variant="badge.variant"
            :removable="badge.removable !== false"
            :loading="badge.loading"
            @remove="onRemoveBadge(badge.id)"
          />
          <Popover v-if="hiddenDocCount > 0">
            <PopoverTrigger as-child>
              <button
                type="button"
                class="whitespace-nowrap text-sm font-normal leading-5 text-gray-500 font-inter hover:text-gray-700"
              >
                +{{ hiddenDocCount }} {{ t('more') }}
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              :side-offset="8"
              class="min-h-[52px] w-[28rem] max-w-[calc(100vw-2rem)] rounded-xl border-0 bg-white px-3 py-2.5
                shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)] outline outline-1
                -outline-offset-1 outline-gray-200"
            >
              <div class="grid grid-cols-2 gap-2">
                <InputBadge
                  v-for="badge in docBadges"
                  :key="badge.id"
                  :label="badge.label"
                  :variant="badge.variant"
                  :removable="badge.removable !== false"
                  :loading="badge.loading"
                  @remove="onRemoveBadge(badge.id)"
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <!-- Non-document badges row (case, court-db) -->
        <div
          v-if="nonDocBadges.length"
          class="flex flex-wrap content-start items-start gap-2"
        >
          <InputBadge
            v-for="badge in nonDocBadges"
            :key="badge.id"
            :label="badge.label"
            :variant="badge.variant"
            :removable="badge.removable !== false"
            :loading="badge.loading"
            @remove="onRemoveBadge(badge.id)"
          />
        </div>
      </div>

      <textarea
        ref="textareaRef"
        v-model="message"
        class="min-h-6 w-full resize-none overflow-y-auto border-none bg-transparent p-0 pe-3 text-base font-normal
          leading-6 text-gray-800 font-inter placeholder:text-gray-400 focus:outline-none focus:ring-0
          disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar]:w-2"
        style="height: 24px; max-height: 270px"
        :placeholder="t('placeholder')"
        :aria-label="t('placeholder')"
        :disabled="props.disabled"
        @keydown="handleKeydown"
      />

      <div class="inline-flex items-center justify-end gap-1 self-stretch">
        <div class="flex items-center justify-start gap-1">
          <Popover v-model:open="plusMenuOpen">
            <PopoverTrigger as-child>
              <button
                type="button"
                class="relative flex h-6 w-6 items-center justify-center overflow-hidden"
                :aria-label="t('addDocuments')"
              >
                <Plus
                  class="h-6 w-6"
                  :class="plusMenuOpen ? 'text-primary' : 'text-gray-600'"
                  :stroke-width="1.3"
                />
              </button>
            </PopoverTrigger>
            <!--
              ``overflow-hidden`` clips inner content to the popover's rounded
              border-radius. Without it, the case-search sub-panel's rectangular
              bottom-fade gradient (and any other rectangular white surfaces inside)
              bleed past the rounded corners and read as a flat rectangular frame
              behind the popover.
            -->
            <PopoverContent
              side="top"
              align="start"
              :side-offset="12"
              class="min-h-[52px] overflow-hidden rounded-xl border-0 bg-white p-0
                shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)] outline outline-1
                -outline-offset-1 outline-gray-200"
              :class="caseSearchOpen ? 'w-72' : 'w-72 py-2.5'"
            >
              <!-- Case search sub-panel -->
              <template v-if="caseSearchOpen">
                <CaseSearchPanel
                  :search-query="caseSearchQuery"
                  @update:search-query="caseSearchQuery = $event"
                  @select="onCaseSelected"
                  @preview="onCasePreview"
                  @back="caseSearchOpen = false"
                />
              </template>

              <!-- Default menu -->
              <template v-else>
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-left hover:bg-gray-50"
                  @click="triggerFilePicker"
                >
                  <Paperclip
                    class="h-5 w-5 shrink-0 text-gray-800"
                    :stroke-width="1.25"
                  />
                  <span class="text-sm font-normal leading-5 text-gray-800 font-inter">
                    {{ t('addDocuments') }}
                  </span>
                </button>

                <!-- Anfrage hinzufügen: normal / locked states -->
                <TooltipProvider
                  v-if="props.caseLocked && props.selectedCase"
                  :delay-duration="300"
                >
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <button
                        type="button"
                        disabled
                        class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-left"
                      >
                        <FolderInput
                          class="h-5 w-5 shrink-0 text-gray-400"
                          :stroke-width="1.25"
                        />
                        <span
                          class="line-clamp-1 flex-1 text-sm font-normal leading-5 text-gray-400 font-inter"
                        >
                          {{ props.selectedCase.label }}
                        </span>
                        <Info
                          class="h-5 w-5 shrink-0 text-gray-800"
                          :stroke-width="1.25"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      class="max-w-[16rem]"
                    >
                      {{ t('caseAlreadyAdded') }}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <!--
                  Pre-send "Add request" affordance.

                  Stays visible whenever the case is not yet locked (post-send) so the user
                  can switch contexts without first having to remove the current badge.
                  ``SidePanelChat.onSelectCase`` already replaces ``manualCase`` and
                  re-loads the case in the unified chat store; the subsequent ``onSend``
                  PATCHes the new ``case_uuid`` onto the session, so a switch flows through
                  end-to-end without extra wiring here.
                -->
                <button
                  v-else
                  type="button"
                  class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-left hover:bg-gray-50"
                  @click="caseSearchOpen = true"
                >
                  <FolderInput
                    class="h-5 w-5 shrink-0 text-gray-800"
                    :stroke-width="1.25"
                  />
                  <span class="text-sm font-normal leading-5 text-gray-800 font-inter">
                    {{ t('addRequest') }}
                  </span>
                </button>

                <div
                  v-if="courtDatabaseFlagEnabled"
                  class="flex items-center gap-2.5 p-4"
                >
                  <Section
                    class="h-5 w-5 shrink-0 text-gray-800"
                    :stroke-width="1.25"
                  />
                  <span class="flex-1 text-sm font-normal leading-5 text-gray-800 font-inter">
                    {{ t('courtDatabase') }}
                  </span>
                  <Switch
                    :checked="props.courtDbEnabled"
                    class="h-5 w-9 data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200"
                    thumb-class="h-4 w-4 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
                    @update:checked="toggleCourtDb"
                  />
                </div>
              </template>
            </PopoverContent>
          </Popover>
          <button
            type="button"
            class="flex items-center justify-start gap-1 rounded-[999px] px-3 py-1 hover:bg-gray-50"
            @click="emit('openPrompts')"
          >
            <div class="relative h-6 w-6 overflow-hidden">
              <ListRestart
                class="h-6 w-6 text-gray-600"
                :stroke-width="1.3"
              />
            </div>
            <div class="justify-start text-sm font-normal leading-5 text-gray-600 font-inter">
              {{ t('prompts') }}
            </div>
          </button>
        </div>
        <div class="relative h-full flex-1 self-stretch" />
        <ModelSelector
          :model-id="modelId ?? null"
          :models="availableModels ?? []"
          :disabled="disabled"
          :chat-context="chatContext"
          @update:model-id="emit('update:modelId', $event)"
        />
        <div class="flex items-center justify-start gap-3">
          <button
            type="button"
            data-property-1="prompt"
            class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-[60px] p-1.5 outline outline-1
              -outline-offset-1 outline-pink-500 transition-opacity"
            :class="canEnhance ? 'cursor-pointer opacity-100' : 'cursor-default opacity-50'"
            :disabled="!canEnhance"
            @click="enhancePrompt"
          >
            <SparkleIcon
              v-if="!isEnhancing"
              class="h-4 w-4"
            />
            <svg
              v-else
              class="h-5 w-5 animate-spin"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="8"
                cy="8"
                r="6"
                stroke="#e5e7eb"
                stroke-width="2"
              />
              <path
                d="M14 8a6 6 0 0 0-6-6"
                stroke="url(#spinner-gradient)"
                stroke-width="2"
                stroke-linecap="round"
              />
              <defs>
                <linearGradient
                  id="spinner-gradient"
                  x1="8"
                  y1="2"
                  x2="14"
                  y2="8"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#EF469B" />
                  <stop
                    offset="1"
                    stop-color="#9333EA"
                  />
                </linearGradient>
              </defs>
            </svg>
          </button>
          <button
            type="button"
            data-property-1="primary"
            class="flex h-8 items-center justify-center overflow-hidden rounded-[60px] bg-primary p-1.5
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
            :class="{ 'opacity-50': !canSend }"
            :disabled="!canSend"
            :aria-disabled="!canSend"
            :aria-label="t('send')"
            @click="handleSend"
          >
            <div class="relative h-5 w-5 overflow-hidden">
              <ArrowUp
                class="h-5 w-5 text-white"
                :stroke-width="1.5"
              />
            </div>
          </button>
        </div>
      </div>
    </div>

    <p class="mt-2 px-1 text-center text-[11px] leading-4 text-gray-400">
      {{ t('legal') }}
    </p>

    <input
      ref="fileInputRef"
      type="file"
      multiple
      :accept="FILE_INPUT_ACCEPT"
      class="hidden"
      @change="onFileInput"
    />

    <CasePreviewDialog
      :open="previewOpen"
      :case-uuid="previewCaseUuid"
      :case-label="previewCaseLabel"
      @update:open="previewOpen = $event"
      @add-to-chat="onPreviewAddToChat"
    />
  </div>
</template>
